import { core, parseDay } from '@map-my-money/engine';
import {
  flowGroups, flowKey, monthlyAfter, nextPaymentDay, type CheckInFlowsBody, type ExpectedFlow, type FlowGroup, type FlowGroupId, type FlowItem, type FlowPayment, type WirePlan,
} from '@map-my-money/views';
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { t, type MessageKey } from '../i18n/index.ts';
import { dayMonth, dayMonthLong, longSpan, money } from '../lib/format.ts';
import { AmountInput, groupedAmount, parseAmount, ungroupedAmount } from './AmountInput.tsx';
import { Icon, type IconName } from './Icon.tsx';

type Added = NonNullable<CheckInFlowsBody['added']>[number];
type Changed = NonNullable<CheckInFlowsBody['changed']>[number];

/** A group folds its small items once it has more than this many. */
const FOLD_AFTER = 8;
/** An item is small when every payment is under this. */
const SMALL = 50;
/** From this many calendar months since the last check-in, step 1 leads with going straight to the balances. */
export const LONG_GAP_MONTHS = 3;
export const isLongGap = (since: string, day: string) => core.addMonths(parseDay(since), LONG_GAP_MONTHS) <= parseDay(day);

const GROUP_STYLE: Readonly<Record<FlowGroupId, { readonly icon: IconName; readonly tone: string; readonly title: MessageKey; readonly chip: MessageKey }>> = {
  in: { icon: 'wallet', tone: 'mint', title: 'flows.group.in', chip: 'flows.chip.in' },
  invested: { icon: 'trendUp', tone: 'violet', title: 'flows.group.invested', chip: 'flows.chip.invested' },
  spent: { icon: 'receipt', tone: 'amber', title: 'flows.group.spent', chip: 'flows.chip.spent' },
  debts: { icon: 'card', tone: 'terracotta', title: 'flows.group.debts', chip: 'flows.chip.debts' },
};

const keyOf = (p: Pick<ExpectedFlow, 'item' | 'date' | 'seq'>) => flowKey(p.item, p.date, p.seq);
const sameRef = (p: Pick<ExpectedFlow, 'item' | 'date' | 'seq'>) => (r: { item: string; date: string; seq?: number | undefined }) => keyOf(p) === flowKey(r.item, r.date, r.seq);
const refOf = (p: Pick<ExpectedFlow, 'item' | 'date' | 'seq'>) => ({ item: p.item, date: p.date, ...(p.seq ? { seq: p.seq } : {}) });
const day = (iso: string) => dayMonth(parseDay(iso));
const spoken = (iso: string) => dayMonthLong(parseDay(iso));

/** The step's edits to the body, each one payment at a time. */
export const flowEdits = {
  changed(body: CheckInFlowsBody, p: ExpectedFlow, amount: string): CheckInFlowsBody {
    const rest = (body.changed ?? []).filter((c) => !sameRef(p)(c));
    const skipped = (body.skipped ?? []).filter((s) => !sameRef(p)(s));
    const same = Number(amount) === Number(p.amount);
    return { ...body, skipped, changed: same ? rest : [...rest, { ...refOf(p), amount }] };
  },
  skipped(body: CheckInFlowsBody, p: ExpectedFlow): CheckInFlowsBody {
    return { ...body, changed: (body.changed ?? []).filter((c) => !sameRef(p)(c)), skipped: [...(body.skipped ?? []).filter((s) => !sameRef(p)(s)), refOf(p)] };
  },
  undone(body: CheckInFlowsBody, p: ExpectedFlow): CheckInFlowsBody {
    return { ...body, changed: (body.changed ?? []).filter((c) => !sameRef(p)(c)), skipped: (body.skipped ?? []).filter((s) => !sameRef(p)(s)) };
  },
  /** "From now on" belongs to the item's latest changed payment. */
  fromNowOn(body: CheckInFlowsBody, item: string, on: boolean): CheckInFlowsBody {
    const ofItem = (body.changed ?? []).filter((c) => c.item === item);
    const latest = ofItem.reduce<Changed | null>((best, c) => (best === null || c.date > best.date ? c : best), null);
    return {
      ...body,
      changed: (body.changed ?? []).map((c): Changed => {
        const { fromNowOn: _, ...plain } = c;
        return c.item === item && c === latest && on ? { ...plain, fromNowOn: true } : plain;
      }),
    };
  },
  added(body: CheckInFlowsBody, entry: Added): CheckInFlowsBody {
    return { ...body, added: [...(body.added ?? []), entry] };
  },
  removed(body: CheckInFlowsBody, index: number): CheckInFlowsBody {
    return { ...body, added: (body.added ?? []).filter((_, i) => i !== index) };
  },
  monthly(body: CheckInFlowsBody, index: number, on: boolean): CheckInFlowsBody {
    return { ...body, added: (body.added ?? []).map((a, i) => (i === index ? { ...a, everyMonth: on } : a)) };
  },
};

export const changeCount = (body: CheckInFlowsBody) => (body.changed?.length ?? 0) + (body.skipped?.length ?? 0) + (body.added?.length ?? 0);

const statusOf = (p: FlowPayment): { text: string; changed: boolean } => {
  if (p.status === 'skipped') return { text: t('flows.status.skipped'), changed: true };
  if (p.status === 'changed') {
    const gap = Number(p.actual) - Number(p.amount);
    return { text: t(gap > 0 ? 'flows.status.more' : 'flows.status.less', { amount: money(Math.abs(gap)) }), changed: true };
  }
  return { text: t('flows.status.expected'), changed: false };
};

function itemSummary(item: FlowItem): string {
  if (item.added) return t('flows.item.oneOff', { date: day(item.added.date) });
  const dates = item.payments.map((p) => day(p.date));
  if (dates.length === 1) return t('flows.item.one', { date: dates[0]! });
  if (dates.length <= 3) return t('flows.item.few', { n: dates.length, dates: dates.join(', ') });
  return t('flows.item.many', { n: dates.length, first: dates[0]!, last: dates[dates.length - 1]! });
}

/** The phone's shorter line: "3 × £3,000" when every payment is the same, else the count. */
function itemShort(item: FlowItem): string {
  if (item.added) return t('flows.item.oneOff', { date: day(item.added.date) });
  const amounts = new Set(item.payments.map((p) => p.amount));
  const n = item.payments.length;
  if (n === 1) return day(item.payments[0]!.date);
  return amounts.size === 1 && item.changed === 0 && item.skipped === 0 ? t('flows.item.times', { n, amount: money(Number(item.payments[0]!.amount)) }) : t('flows.item.count', { n });
}

function itemStatus(item: FlowItem): { text: string; changed: boolean } {
  if (item.added) return { text: t(item.added.everyMonth ? 'flows.status.monthly' : 'flows.status.added'), changed: true };
  const parts = [
    ...(item.changed > 0 ? [t('flows.item.changed', { n: item.changed })] : []),
    ...(item.skipped > 0 ? [t(item.payments.length === 1 ? 'flows.status.skipped' : 'flows.item.skipped', { n: item.skipped })] : []),
  ];
  return parts.length > 0 ? { text: parts.join(' · '), changed: true } : { text: t('flows.status.expected'), changed: false };
}

const detailOf = (p: FlowPayment): string | null => {
  if (p.interest !== undefined && p.principal !== undefined && p.status !== 'skipped') {
    const interest = Number(p.interest);
    const principal = Number(p.actual) - interest;
    return t('flows.debt.split', { principal: money(principal), interest: money(interest) });
  }
  if (p.employer && p.relief) return t('flows.pension.both', { own: money(Number(p.amount)), employer: money(Number(p.employer)), relief: money(Number(p.relief)) });
  if (p.employer) return t('flows.pension.split', { own: money(Number(p.amount)), employer: money(Number(p.employer)) });
  if (p.relief) return t('flows.pension.relief', { own: money(Number(p.amount)), relief: money(Number(p.relief)) });
  return null;
};

interface AddDraft {
  readonly label: string;
  readonly amount: string;
  readonly date: string;
  readonly kind: 'income' | 'expense';
  readonly tried: boolean;
}

/**
 * Step 1 of a check-in (R27): the payments the map expected since the last check-in, grouped, one row
 * per item; each payment can be changed or marked as didn't happen, and anything else added. Only the
 * exceptions are kept in `body`; everything else counts as expected.
 */
export function WhatHappened({ headingId, plan, date, window: span, expected, body, onChange, first, monthsSince, longGap, cash, mapCash, announce }: {
  headingId: string;
  plan: WirePlan;
  date: string;
  window: { readonly from: string; readonly to: string; readonly previous: string | null };
  expected: readonly ExpectedFlow[];
  body: CheckInFlowsBody;
  onChange: (body: CheckInFlowsBody) => void;
  first: boolean;
  monthsSince: number;
  longGap: boolean;
  /** What cash should be with the changes, and what the map alone said. */
  cash: string;
  mapCash: string;
  announce: (text: string) => void;
}) {
  const id = useId();
  const groups = useMemo(() => flowGroups(expected, body), [expected, body]);
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [unfolded, setUnfolded] = useState<ReadonlySet<FlowGroupId>>(new Set());
  const [editing, setEditing] = useState<{ key: string; text: string; invalid: boolean } | null>(null);
  /** The latest payment's amount, typed on its collapsed row. */
  const [quick, setQuick] = useState<{ key: string; text: string; invalid: boolean } | null>(null);
  /** Each row is one tab stop: the control last focused in it, by row. */
  const [roved, setRoved] = useState<ReadonlyMap<string, string>>(new Map());
  const [draft, setDraft] = useState<AddDraft | null>(null);
  const [offer, setOffer] = useState<number | null>(null);
  const focusTarget = useRef<string | null>(null);
  const buttons = useRef(new Map<string, HTMLElement | null>());

  useEffect(() => {
    if (focusTarget.current === null) return;
    buttons.current.get(focusTarget.current)?.focus();
    focusTarget.current = null;
  });

  const nextDay = useMemo(() => {
    const byId = new Map((plan.transactions ?? []).map((tx) => [tx.id, tx]));
    return (item: string) => {
      const tx = byId.get(item);
      return tx ? nextPaymentDay(tx, date) : null;
    };
  }, [plan, date]);

  const since = span.previous ?? plan.configuration.initialBalanceDate ?? span.from;
  const title = span.previous ? t('flows.title.since', { date: spoken(span.previous) }) : t('flows.title.setup', { date: spoken(since) });
  const lead = expected.length === 1 ? t('flows.lead.one') : t('flows.lead', { n: expected.length });

  const validAmount = (text: string) => {
    const amount = parseAmount(text);
    return amount === null || Number.isNaN(amount) || amount < 0 ? null : amount;
  };

  const record = (p: FlowPayment, amount: number) => {
    onChange(flowEdits.changed(body, p, String(Math.round(amount * 100) / 100)));
    const gap = amount - Number(p.amount);
    announce(Math.round(gap * 100) === 0
      ? t('flows.announce.undone', { label: p.label, date: spoken(p.date) })
      : t('flows.announce.changed', { label: p.label, date: spoken(p.date), amount: money(amount), diff: t(gap > 0 ? 'flows.status.more' : 'flows.status.less', { amount: money(Math.abs(gap)) }) }));
  };

  const commitEdit = (p: FlowPayment) => {
    if (!editing) return;
    const amount = validAmount(editing.text);
    if (amount === null) {
      setEditing({ ...editing, invalid: true });
      return;
    }
    record(p, amount);
    focusTarget.current = `change:${p.key}`;
    setEditing(null);
  };

  /** Keeps a typed latest amount (on Enter, Ctrl/Cmd+Enter or leaving the field); one that isn't a number is refused on Enter and dropped on leaving. */
  const commitQuick = (p: FlowPayment, strict: boolean) => {
    if (quick?.key !== p.key) return;
    const amount = validAmount(quick.text);
    if (amount === null) {
      setQuick(strict ? { ...quick, invalid: true } : null);
      return;
    }
    if (Math.round(amount * 100) !== Math.round(Number(p.status === 'changed' ? p.actual : p.amount) * 100)) record(p, amount);
    setQuick(null);
  };

  const quickKeys = (p: FlowPayment) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (!e.ctrlKey && !e.metaKey) e.preventDefault();
      commitQuick(p, !e.ctrlKey && !e.metaKey);
    } else if (e.key === 'Escape' && quick?.key === p.key) {
      e.preventDefault();
      e.stopPropagation();
      setQuick(null);
      const field = e.currentTarget;
      requestAnimationFrame(() => field.select());
    }
  };

  /** A row's controls for a roving tab stop: the one last focused (else the first) is in the tab order, arrows move between them. */
  const rover = (row: string, controls: readonly string[]) => {
    const remembered = roved.get(row);
    const current = remembered !== undefined && controls.includes(remembered) ? remembered : controls[0];
    return (control: string) => ({
      tabIndex: control === current ? 0 : -1,
      'data-rove': control,
      onFocus: () => setRoved((m) => (m.get(row) === control ? m : new Map(m).set(row, control))),
    });
  };

  const roveKeys = (e: KeyboardEvent<HTMLElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key) || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    const target = e.target as HTMLElement;
    const controls = [...e.currentTarget.querySelectorAll<HTMLElement>('[data-rove]')];
    const at = controls.indexOf(target);
    if (at < 0) return;
    if (target instanceof HTMLInputElement) {
      const { selectionStart, selectionEnd, value } = target;
      const atStart = selectionStart === 0 && selectionEnd === 0;
      const atEnd = selectionStart === value.length && selectionEnd === value.length;
      if (e.key === 'Home' || e.key === 'End' || (e.key === 'ArrowLeft' && !atStart) || (e.key === 'ArrowRight' && !atEnd)) return;
    }
    const to = e.key === 'Home' ? 0 : e.key === 'End' ? controls.length - 1 : at + (e.key === 'ArrowRight' ? 1 : -1);
    if (to < 0 || to >= controls.length || to === at) return;
    e.preventDefault();
    controls[to]!.focus();
  };

  const editKeys = (p: FlowPayment) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      commitEdit(p);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      focusTarget.current = `change:${p.key}`;
      setEditing(null);
    }
  };

  const toggle = (key: string) => setOpen((s) => {
    const next = new Set(s);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  const addEntry = () => {
    if (!draft) return;
    const amount = parseAmount(draft.amount);
    const ok = draft.label.trim().length > 0 && amount !== null && !Number.isNaN(amount) && amount > 0 && draft.date >= span.from && draft.date <= span.to;
    if (!ok) {
      setDraft({ ...draft, tried: true });
      return;
    }
    onChange(flowEdits.added(body, { kind: draft.kind, label: draft.label.trim(), date: draft.date, amount: ungroupedAmount(draft.amount) }));
    setOffer(body.added?.length ?? 0);
    announce(t('flows.announce.added', { label: draft.label.trim() }));
    setDraft(null);
    setOpen((s) => new Set(s));
  };

  const jumpTo = (g: FlowGroupId) => {
    const heading = document.getElementById(`${id}-group-${g}`);
    heading?.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    heading?.focus({ preventScroll: true });
  };

  const changes = changeCount(body);
  const cashMoved = Math.round(Number(cash)) !== Math.round(Number(mapCash));

  if (expected.length === 0) {
    return (
      <div className="flows">
        <div className="flows__caught-up">
          <span className="flows__caught-up-tick" aria-hidden="true"><Icon name="check" size={22} strokeWidth={3} /></span>
          <h2 id={headingId} className="checkin__title" tabIndex={-1}>{t('flows.caughtUp.title')}</h2>
          <p className="checkin__lead">{span.previous ? t('flows.caughtUp.since', { date: spoken(span.previous) }) : t('flows.caughtUp.setup', { date: spoken(since) })}</p>
        </div>
        {groups.length > 0 && groupsList()}
        {addArea(t('flows.add.caughtUp'))}
      </div>
    );
  }

  return (
    <div className="flows">
      <div className="checkin__intro">
        <h2 id={headingId} className="checkin__title" tabIndex={-1}>{title}</h2>
        <p className="flows__lead">{first ? t('flows.lead.first') : lead}</p>
        {longGap && <p className="flows__note" data-testid="flows-long-gap">{t('flows.lead.long', { span: longSpan(monthsSince) })}</p>}
      </div>
      <ul className="flows__chips" aria-label={t('flows.chips')}>
        {groups.map((g) => (
          <li key={g.id}>
            <button
              type="button" className={`flows__chip flows__chip--${GROUP_STYLE[g.id].tone}`} onClick={() => jumpTo(g.id)}
              aria-label={t('flows.chip.label', { group: t(GROUP_STYLE[g.id].title), n: g.count, amount: money(Number(g.total)) })}
            >
              <span>{t(GROUP_STYLE[g.id].chip)}</span>
              <strong>{money(Number(g.total))}</strong>
              <span className="flows__chip-count">{g.count}</span>
            </button>
          </li>
        ))}
      </ul>
      <div className="flows__layout">
        <div className="flows__main">
          {groupsList()}
          {addArea(t('flows.add'))}
        </div>
        <aside className="flows__changes" aria-labelledby={`${id}-changes`} data-testid="flows-changes">
          <h3 id={`${id}-changes`} className="checkin__group-title">{t('flows.changes.title')}</h3>
          {changes === 0 ? (
            <p className="flows__changes-none">{t('flows.changes.none')}</p>
          ) : (
            <ul className="flows__changes-list">
              {groups.flatMap((g) => g.items).flatMap((item) => changeLines(item)).sort((a, b) => String(a.props['data-date']).localeCompare(String(b.props['data-date'])))}
            </ul>
          )}
          {(changes > 0 || cashMoved) && (
            <p className="flows__changes-cash" data-testid="flows-cash">{t('flows.changes.cash', { amount: money(Math.round(Number(cash))), map: money(Math.round(Number(mapCash))) })}</p>
          )}
        </aside>
      </div>
    </div>
  );

  function changeLines(item: FlowItem) {
    if (item.added) {
      return [(
        <li key={item.item} className="flows__change" data-date={item.added.date}>
          <span>{t('flows.changes.row', { label: item.label, date: day(item.added.date) })}</span>
          <strong>{money(Number(item.added.amount) * (item.kind === 'income' ? 1 : -1), { sign: true })}</strong>
        </li>
      )];
    }
    return item.payments.filter((p) => p.status !== 'confirmed').map((p) => {
      const out = p.direction === 'out';
      const value = p.status === 'skipped'
        ? t(out ? 'flows.changes.kept' : 'flows.changes.missed', { amount: money(Number(p.amount)) })
        : money(Number(p.actual) - Number(p.amount), { sign: true });
      return (
        <li key={p.key} className="flows__change" data-date={p.date}>
          <span>{t(p.status === 'skipped' ? 'flows.changes.skipped' : 'flows.changes.row', { label: p.label, date: day(p.date) })}</span>
          <strong>{value}</strong>
        </li>
      );
    });
  }

  function groupsList() {
    return groups.map((g) => {
      const style = GROUP_STYLE[g.id];
      const small = g.items.length > FOLD_AFTER ? g.items.filter((i) => !i.added && i.changed === 0 && i.skipped === 0 && i.payments.every((p) => Number(p.amount) < SMALL)) : [];
      const folded = small.length >= 3 && !unfolded.has(g.id) ? new Set(small) : new Set<FlowItem>();
      const shown = g.items.filter((i) => !folded.has(i));
      return (
        <section key={g.id} className="flows__group" aria-labelledby={`${id}-group-${g.id}`} data-testid={`flows-group-${g.id}`}>
          <h3 id={`${id}-group-${g.id}`} className="flows__group-head" tabIndex={-1}>
            <span className="flows__group-title">{t(style.title)}</span>
            <span className="flows__group-count">{g.happened === g.count ? t('flows.group.count', { n: g.count }) : t('flows.group.countOf', { happened: g.happened, n: g.count })}</span>
            <span className={`flows__group-total flows__group-total--${style.tone}`}>{money(Number(g.total))}</span>
          </h3>
          <ul className="flows__items">
            {shown.map((item) => itemRow(g, item))}
            {folded.size > 0 && (
              <li className="flows__item flows__item--folded">
                <button type="button" className="flows__row" onClick={() => setUnfolded((s) => new Set(s).add(g.id))} data-testid="flows-small">
                  <span className="flows__icon flows__icon--quiet" aria-hidden="true"><Icon name="bars" size={16} /></span>
                  <span className="flows__row-text">
                    <span className="flows__row-label">{t('flows.small', { n: folded.size })}</span>
                    <span className="flows__row-sub">{t('flows.small.hint', { amount: money(SMALL), total: money([...folded].reduce((s, i) => s + Number(i.total), 0)) })}</span>
                  </span>
                  <span className="flows__row-end"><span className="flows__show">{t('flows.small.show')}</span></span>
                </button>
              </li>
            )}
          </ul>
        </section>
      );
    });
  }

  function itemRow(g: FlowGroup, item: FlowItem) {
    const style = GROUP_STYLE[g.id];
    const rowKey = `${g.id}|${item.item}`;
    const status = itemStatus(item);
    const isOpen = open.has(rowKey);
    const panelId = `${id}-${rowKey.replace(/[^a-zA-Z0-9-]/g, '_')}`;
    if (item.added) {
      const index = item.added.index;
      return (
        <li key={rowKey} className="flows__item flows__item--added" data-testid="flows-added">
          <div className="flows__row flows__row--static">
            <span className="flows__icon flows__icon--cyber" aria-hidden="true"><Icon name="plus" size={16} /></span>
            <span className="flows__row-text">
              <span className="flows__row-label">{item.label}</span>
              <span className="flows__row-sub">{itemSummary(item)}</span>
            </span>
            <span className="flows__row-end">
              <span className="flows__row-amount">{money(Number(item.total))}</span>
              <span className="flows__status flows__status--changed">{status.text}</span>
            </span>
          </div>
          <div className="flows__added-actions">
            {offer === index && !item.added.everyMonth && (
              <p className="flows__offer" data-testid="flows-monthly-offer">
                <Icon name="plusCircle" size={15} />
                <span>{t('flows.monthly.ask')}</span>
                <button type="button" className="text-link" onClick={() => { onChange(flowEdits.monthly(body, index, true)); setOffer(null); }}>{t('flows.monthly.action')}</button>
              </p>
            )}
            {item.added.everyMonth && (
              <p className="flows__offer flows__offer--on" data-testid="flows-monthly-on">
                <Icon name="check" size={14} strokeWidth={3} />
                <span>{t('flows.monthly.on', { date: day(monthlyAfter(item.added.date, date)) })}</span>
                <button type="button" className="text-link" onClick={() => onChange(flowEdits.monthly(body, index, false))}>{t('common.undo')}</button>
              </p>
            )}
            <button
              type="button" className="flows__action flows__action--quiet" aria-label={t('flows.action.remove.label', { label: item.label })}
              onClick={() => { onChange(flowEdits.removed(body, index)); setOffer(null); announce(t('flows.announce.removed', { label: item.label })); }}
            >
              {t('common.delete')}
            </button>
          </div>
        </li>
      );
    }
    const latestChanged = item.payments.filter((p) => p.status === 'changed').at(-1) ?? null;
    const latest = item.payments.at(-1)!;
    const inPlace = latest.status !== 'skipped';
    const rove = rover(rowKey, inPlace ? ['row', 'amount'] : ['row']);
    const drafting = quick?.key === latest.key;
    const amountRove = rove('amount');
    const latestNames = { label: latest.label, date: spoken(latest.date) };
    return (
      <li key={rowKey} className={`flows__item ${isOpen ? 'flows__item--open' : ''}`} data-testid={`flows-item-${item.item}`}>
        <div className="flows__head" onKeyDown={roveKeys}>
          <button
            type="button" className="flows__row" aria-expanded={isOpen} aria-controls={panelId} onClick={() => toggle(rowKey)}
            ref={(el) => { buttons.current.set(`row:${rowKey}`, el); }} {...rove('row')}
          >
            <span className={`flows__icon flows__icon--${style.tone}`} aria-hidden="true"><Icon name={style.icon} size={16} /></span>
            <span className="flows__row-text">
              <span className="flows__row-label">{item.label}</span>
              <span className="flows__row-sub">
                <span className="flows__row-sub--long">{itemSummary(item)}</span>
                <span className="flows__row-sub--short" aria-hidden="true">{itemShort(item)}</span>
                <span className={`flows__status ${status.changed ? 'flows__status--changed' : ''}`}>
                  {!status.changed && <Icon name="check" size={12} strokeWidth={3} />}
                  {status.text}
                </span>
              </span>
            </span>
            <Icon name="chevronDown" size={16} className="flows__chevron" />
          </button>
          <span className="flows__latest" data-testid="flows-latest">
            {inPlace ? (
              <AmountInput
                block="flow-amount" className={`flow-amount--latest ${latest.status === 'changed' ? 'flow-amount--changed' : ''} ${drafting && quick.invalid ? 'flow-amount--invalid' : ''}`}
                value={drafting ? quick.text : groupedAmount(latest.status === 'changed' ? latest.actual : latest.amount)}
                onChange={(text) => setQuick({ key: latest.key, text, invalid: false })}
                {...amountRove} onKeyDown={quickKeys(latest)} onBlur={() => commitQuick(latest, false)}
                onFocus={(e) => { amountRove.onFocus(); e.currentTarget.select(); }}
                aria-label={t('flows.latest.label', latestNames)} aria-invalid={(drafting && quick.invalid) || undefined}
                aria-describedby={drafting && quick.invalid ? `${id}-${latest.key}-latest-error` : undefined}
              />
            ) : (
              <span className="flows__latest-skipped">{money(Number(latest.amount))}</span>
            )}
            <span className="flows__latest-date" aria-hidden="true">{day(latest.date)}</span>
          </span>
        </div>
        {drafting && quick.invalid && <p id={`${id}-${latest.key}-latest-error`} className="checkin__refusal flows__latest-error">{t('flows.amount.invalid')}</p>}
        {!isOpen && latestChanged && fromNowOnTick(item, latestChanged)}
        {isOpen && (
          <ul className="flows__payments" id={panelId}>
            {item.payments.map((p) => paymentLine(item, p, p === latestChanged))}
          </ul>
        )}
      </li>
    );
  }

  function fromNowOnTick(item: FlowItem, p: FlowPayment) {
    const next = nextDay(item.item);
    if (!next || p.status !== 'changed' || editing?.key === p.key) return null;
    const ticked = (body.changed ?? []).some((c) => sameRef(p)(c) && c.fromNowOn);
    return (
      <label className="flows__from-now-on" data-testid="flows-from-now-on">
        <input type="checkbox" checked={ticked} onChange={(e) => onChange(flowEdits.fromNowOn(body, item.item, e.target.checked))} />
        <span>
          <span className="flows__from-now-on-label">{t('flows.fromNowOn', { label: p.label, amount: money(Number(p.actual)) })}</span>
          <span className="flows__from-now-on-hint">{t('flows.fromNowOn.hint', { date: day(next) })}</span>
        </span>
      </label>
    );
  }

  function paymentLine(item: FlowItem, p: FlowPayment, offersFromNowOn: boolean) {
    const status = statusOf(p);
    const isEditing = editing?.key === p.key;
    const detail = detailOf(p);
    const names = { label: p.label, date: spoken(p.date) };
    const controls = p.status === 'confirmed' ? ['change', 'skip'] : p.status === 'changed' ? ['change', 'undo'] : ['undo'];
    const rove = rover(`payment:${p.key}`, controls);
    return (
      <li key={p.key} className={`flows__payment ${p.status !== 'confirmed' ? 'flows__payment--changed' : ''}`} data-testid="flows-payment">
        <div className="flows__payment-main">
          <span className="flows__payment-date">{day(p.date)}</span>
          <span className="flows__payment-amount">
            {p.status === 'changed' && <s className="flows__was">{money(Number(p.amount))}</s>}
            <span className={p.status === 'skipped' ? 'flows__skipped-amount' : ''}>{money(Number(p.status === 'changed' ? p.actual : p.amount))}</span>
          </span>
          <span className={`flows__status ${status.changed ? 'flows__status--changed' : ''}`} data-testid="flows-payment-status">{status.text}</span>
        </div>
        {detail && <p className="flows__payment-detail">{detail}</p>}
        {isEditing ? (
          <div className="flows__edit">
            <AmountInput
              block="flow-amount" className={editing.invalid ? 'flow-amount--invalid' : undefined} value={editing.text}
              onChange={(text) => setEditing({ key: p.key, text, invalid: false })} onKeyDown={editKeys(p)}
              aria-label={t('flows.amount.label', names)} aria-describedby={`${id}-${p.key}-hint`} aria-invalid={editing.invalid || undefined}
              inputRef={(el) => {
                if (el && !el.dataset.selected) {
                  el.dataset.selected = '1';
                  el.focus();
                  el.select();
                }
              }}
            />
            <button type="button" className="button button--primary flows__save" onClick={() => commitEdit(p)}>{t('common.save')}</button>
            <button type="button" className="button button--ghost flows__cancel" onClick={() => { focusTarget.current = `change:${p.key}`; setEditing(null); }}>{t('common.cancel')}</button>
            <span id={`${id}-${p.key}-hint`} className={editing.invalid ? 'checkin__refusal' : 'flows__edit-hint'}>{editing.invalid ? t('flows.amount.invalid') : t('flows.amount.hint')}</span>
          </div>
        ) : (
          <div className="flows__actions" onKeyDown={roveKeys}>
            {p.status === 'confirmed' ? (
              <>
                <button
                  type="button" className="flows__action" aria-label={t('flows.action.change.label', names)}
                  ref={(el) => { buttons.current.set(`change:${p.key}`, el); }} {...rove('change')}
                  onClick={() => setEditing({ key: p.key, text: groupedAmount(p.amount), invalid: false })}
                >
                  <Icon name="pen" size={14} />{t('flows.action.change')}
                </button>
                <button
                  type="button" className="flows__action" aria-label={t('flows.action.skip.label', names)}
                  ref={(el) => { buttons.current.set(`skip:${p.key}`, el); }} {...rove('skip')}
                  onClick={() => { onChange(flowEdits.skipped(body, p)); announce(t('flows.announce.skipped', names)); focusTarget.current = `undo:${p.key}`; }}
                >
                  <Icon name="close" size={14} />{t('flows.action.skip')}
                </button>
              </>
            ) : (
              <>
                {p.status === 'changed' && (
                  <button
                    type="button" className="flows__action" aria-label={t('flows.action.change.label', names)}
                    ref={(el) => { buttons.current.set(`change:${p.key}`, el); }} {...rove('change')}
                    onClick={() => setEditing({ key: p.key, text: groupedAmount(p.actual), invalid: false })}
                  >
                    <Icon name="pen" size={14} />{t('flows.action.change')}
                  </button>
                )}
                <button
                  type="button" className="flows__action flows__action--undo" aria-label={t('flows.action.undo.label', names)}
                  ref={(el) => { buttons.current.set(`undo:${p.key}`, el); }} {...rove('undo')}
                  onClick={() => { onChange(flowEdits.undone(body, p)); announce(t('flows.announce.undone', names)); focusTarget.current = `change:${p.key}`; }}
                >
                  {t('common.undo')}
                </button>
              </>
            )}
          </div>
        )}
        {offersFromNowOn && fromNowOnTick(item, p)}
      </li>
    );
  }

  function addArea(label: string) {
    if (!draft) {
      return (
        <button
          type="button" className="flows__add" data-testid="flows-add"
          ref={(el) => { buttons.current.set('add', el); }}
          onClick={() => setDraft({ label: '', amount: '', date: span.to, kind: 'expense', tried: false })}
        >
          <Icon name="plusCircle" size={18} />{label}
        </button>
      );
    }
    const amount = parseAmount(draft.amount);
    const labelError = draft.tried && draft.label.trim() === '' ? t('flows.add.needsLabel') : null;
    const amountError = draft.tried && (amount === null || Number.isNaN(amount) || amount <= 0) ? t('flows.add.needsAmount') : null;
    const dateError = draft.tried && (draft.date < span.from || draft.date > span.to) ? t('flows.add.needsDate', { from: day(span.from), to: day(span.to) }) : null;
    const submitOnEnter = (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        addEntry();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        focusTarget.current = 'add';
        setDraft(null);
      }
    };
    return (
      <div className="flows__add-form" role="group" aria-labelledby={`${id}-add-title`} data-testid="flows-add-form" onKeyDown={submitOnEnter}>
        <h3 id={`${id}-add-title`} className="checkin__group-title">{t('flows.add.title')}</h3>
        <div className="flows__direction" role="radiogroup" aria-label={t('flows.add.direction')}>
          {(['expense', 'income'] as const).map((kind) => (
            <label key={kind} className={`flows__direction-option ${draft.kind === kind ? 'flows__direction-option--on' : ''}`}>
              <input type="radio" name={`${id}-direction`} checked={draft.kind === kind} onChange={() => setDraft({ ...draft, kind })} />
              {t(kind === 'expense' ? 'flows.add.out' : 'flows.add.in')}
            </label>
          ))}
        </div>
        <div className="flows__add-fields">
          <label className="flows__field flows__field--label">
            <span className="field__label">{t('flows.add.label')}</span>
            <input
              className="input" value={draft.label} maxLength={80} placeholder={t('flows.add.placeholder')} autoFocus
              aria-invalid={labelError ? true : undefined} onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            />
            {labelError && <span className="checkin__refusal">{labelError}</span>}
          </label>
          <label className="flows__field">
            <span className="field__label">{t('flows.add.amount')}</span>
            <AmountInput block="flow-amount" className={amountError ? 'flow-amount--invalid' : undefined} value={draft.amount} onChange={(text) => setDraft({ ...draft, amount: text })} aria-invalid={amountError ? true : undefined} />
            {amountError && <span className="checkin__refusal">{amountError}</span>}
          </label>
          <label className="flows__field">
            <span className="field__label">{t('flows.add.date')}</span>
            <input className="input" type="date" min={span.from} max={span.to} value={draft.date} aria-invalid={dateError ? true : undefined} onChange={(e) => setDraft({ ...draft, date: e.target.value })} />
            {dateError && <span className="checkin__refusal">{dateError}</span>}
          </label>
        </div>
        <div className="flows__add-actions">
          <button type="button" className="button button--ghost" onClick={() => { focusTarget.current = 'add'; setDraft(null); }}>{t('common.cancel')}</button>
          <button type="button" className="button button--primary" onClick={addEntry} data-testid="flows-add-save">{t('flows.add.save')}</button>
        </div>
      </div>
    );
  }
}

const prefersReducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
